import { useCallback } from 'react';
import { useAppSelector } from 'src/utils/Hooks';
import AdvancedSearchRequestBuilder, {
  useCreateAdvancedSearchRequestBuilder
} from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { getAppName } from 'src/utils/app';
import { useBeaconFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBeaconFilters';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import {
  buildBUEntityConditions,
  buildBusinessUnitConditionsV2
} from 'src/components/EntityPage/Filters/businessUnitFilters';
import { removeRetailPricesForBusinessUnits } from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/utils';
import { useAllSegmentsAndSearchTermLists, useBrandsFollowingIds, useCategoryIds } from 'src/utils/Hooks/reduxSelectors';
import {
  UseAggregationBuilderWithMetricsArgs,
  useCreateAggregationBuilderWithMetrics
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy';

export interface UseBaseMetricRequestBuilderProps extends UseAggregationBuilderWithMetricsArgs {
  requestId: string;
  startWeekId?: number;
  endWeekId?: number;
  sortFieldName?: string;
  buildAggregationBuilder?: (aggregationBuilder: AggregationBuilder) => AggregationBuilder;
}

/**
 * Returns a function that applies filters depending on the entity type.
 */
export const useAtlasV2ApplyEntityFilters = () => {
  const entityType = useAppSelector((state) => state?.entityService?.mainEntity?.type);
  const retailer = useAppSelector((state) => state.retailer);
  const entityId = useAppSelector((state) => state?.entityService?.mainEntity?.id);
  const brandIds = useBrandsFollowingIds();
  const categoryIds = useCategoryIds();

  const { hasActiveFilters, appFilters } = useBeaconFilters();

  const segments = useAllSegmentsAndSearchTermLists();

  const { businessUnits, savedSearchesById } = useAppSelector((state) => state.segments);

  return useCallback(
    (requestBuilder: AdvancedSearchRequestBuilder) => {
      requestBuilder.addConditionTermFilter('categoryId', 'should', categoryIds);
      requestBuilder.addConditionTermFilter('brandId', 'should', brandIds);

      if (entityType === 'product') {
        requestBuilder
          .addConditionTermFilter('stacklineSku', 'should', [entityId])
          .removeConditionTermFilters((field) => field.fieldName === 'brandId');
      } else if (entityType === 'brand') {
        requestBuilder
          .removeConditionTermFilters((filter) => filter.fieldName === 'brandId')
          .addConditionTermFilter('brandId', 'should', [entityId]);
      } else if (entityType === 'category') {
        if (entityId !== '0') {
          requestBuilder.addConditionTermFilter('categoryId', 'should', [entityId]);
        }
        requestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
      } else if (entityType === 'subcategory') {
        requestBuilder
          .addConditionTermFilter('subCategoryId', 'should', [entityId])
          .removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
      } else if (entityType === 'segment' || entityType === 'searchtermlist') {
        const segment = segments.find((seg) => seg.id === entityId);

        if (segment) {
          requestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
          if (segment.conditions.rangeFilters) {
            segment.conditions.rangeFilters.forEach((rangeFilter) => {
              requestBuilder.replaceConditionRangeFilter(
                rangeFilter.fieldName,
                rangeFilter.minValue,
                rangeFilter.maxValue
              );
            });
          }
          if (segment.conditions.termFilters) {
            segment.conditions.termFilters.forEach((termFilter) => {
              requestBuilder.replaceConditionTermFilter(termFilter.fieldName, termFilter.condition, termFilter.values);
            });
          }
        }
      } else if (entityType === 'businessunit') {
        const selectedBusinessUnit = businessUnits.find((businessUnit) => businessUnit.id === entityId);
        const nestedFilterConditions = buildBUEntityConditions(savedSearchesById, selectedBusinessUnit);

        if (selectedBusinessUnit) {
          requestBuilder
            .removeConditionTermFilters((filter) => filter.fieldName === 'brandId')
            .addPreCalculatedNestedFilterCondition(removeRetailPricesForBusinessUnits(nestedFilterConditions));
        }
      }

      if (hasActiveFilters) {
        const { categoryFilters, subcategoryFilters, segmentFilters, businessUnitFilters } = appFilters;

        if (categoryFilters) {
          requestBuilder.replaceConditionTermFilter('categoryId', 'should', categoryFilters);
        }
        if (subcategoryFilters) {
          requestBuilder.replaceConditionTermFilter('subCategoryId', 'should', subcategoryFilters);
        }
        if (segmentFilters) {
          // TODO update segment filters to use range filters too, @Jonny
          const { termFilters } = segmentFilters;
          termFilters.forEach(({ fieldName, condition, values }) =>
            requestBuilder.addConditionTermFilter(fieldName, condition, values)
          );
        }

        if (businessUnitFilters) {
          const selectedBusinessUnits =
            entityType === 'businessunit'
              ? new Set([entityId as string, ...businessUnitFilters])
              : new Set(businessUnitFilters);

          const nestedFilterConditions = buildBusinessUnitConditionsV2(
            businessUnits,
            savedSearchesById!,
            retailer,
            selectedBusinessUnits
          );

          requestBuilder.addPreCalculatedNestedFilterCondition(
            removeRetailPricesForBusinessUnits(nestedFilterConditions)
          );
        }
      }
      // TODO: If we ever come across empty aggregations, we need to add them to conditions (currently only in Promotions)
      if (appFilters.retailPriceFilter) {
        const minValue = appFilters.retailPriceFilter?.minValue || 0;
        const maxValue = appFilters.retailPriceFilter?.maxValue || 1000000;
        requestBuilder.modifyAggregation((aggBuilder) => {
          aggBuilder.replaceConditionRangeFilter('retailPrice', +minValue, +maxValue);
          return aggBuilder;
        });
      } else {
        requestBuilder.modifyAggregation((aggBuilder) => {
          aggBuilder.removeConditionRangeFilters('retailPrice');
          return aggBuilder;
        });
      }

      return requestBuilder;
    },
    [
      appFilters,
      businessUnits,
      categoryIds,
      entityId,
      entityType,
      hasActiveFilters,
      retailer,
      savedSearchesById,
      segments
    ]
  );
};

/**
 * Returns a factory function for getting a new request builder.
 */
export function useCreateAtlasV2BaseMetricRequestBuilder() {
  const applyEntityFilters = useAtlasV2ApplyEntityFilters();

  const createAdvancedSearchRequestBuilder = useCreateAdvancedSearchRequestBuilder();
  const createAggregationBuilderWithMetrics = useCreateAggregationBuilderWithMetrics();

  return useCallback(
    ({
      requestId,
      startWeekId,
      endWeekId,
      indexName,
      fields,
      groupByFieldName,
      sortFieldName,
      buildAggregationBuilder
    }: UseBaseMetricRequestBuilderProps) => {
      const requestBuilder = createAdvancedSearchRequestBuilder(requestId, `${getAppName()}-${indexName}`);
      const aggregationBuilder = createAggregationBuilderWithMetrics({
        indexName,
        fields,
        groupByFieldName
      });

      if (startWeekId) {
        aggregationBuilder.addConditionRangeFilter('weekId', startWeekId, endWeekId);
      }

      if (endWeekId && !startWeekId) {
        aggregationBuilder.addConditionRangeFilter('weekId', undefined, endWeekId);
      }

      const sortField = sortFieldName ? INDEX_FIELDS.getField(getAppName(), indexName, sortFieldName) : undefined;
      if (sortField) {
        aggregationBuilder.setSortByAggregationField(
          sortField.displayName,
          sortField.name,
          sortField.aggregationFunction,
          true
        );
      }

      const aggBuilderToUse = buildAggregationBuilder
        ? buildAggregationBuilder(aggregationBuilder)
        : aggregationBuilder;

      requestBuilder
        .setPageNumber(1)
        .setPageSize(1200)
        .setPeriod('year')
        .setDoAggregation(true)
        .setReturnDocuments(false)
        .addAggregation(aggBuilderToUse.build())
        .setSearchBy('parent');

      if (startWeekId) {
        requestBuilder.addConditionRangeFilter('weekId', startWeekId, endWeekId);
      }

      if (endWeekId && !startWeekId) {
        requestBuilder.addConditionRangeFilter('weekId', undefined, endWeekId);
      }

      if (sortField) {
        requestBuilder.addSortField(sortField.displayName, sortField.name, sortField.aggregationFunction, true);
      }

      requestBuilder.apply(applyEntityFilters);

      return requestBuilder;
    },
    [applyEntityFilters, createAdvancedSearchRequestBuilder, createAggregationBuilderWithMetrics]
  );
}

/**
 * Creates the base request builder for a given
 * list of metric fields.
 *
 * Should be used multiple times when there are metrics with different indices.
 * For example, 'sales -> unitsSold' and 'traffic -> totalClicks' are under
 * two different APIs, so they should be requested separately.
 */
export const useAtlasV2BaseMetricRequestBuilder = (
  args: UseBaseMetricRequestBuilderProps
): AdvancedSearchRequestBuilder => {
  const createBaseMetricRequestBuilder = useCreateAtlasV2BaseMetricRequestBuilder();
  return createBaseMetricRequestBuilder(args);
};
